import { ChangeDetectionStrategy, Component, HostListener, OnInit, signal } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { HeaderComponent } from '../header/header.component';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { ToastComponent } from '../../../shared/components/toast/toast.component';

/**
 * Provides the main layout structure for the dashboard.
 * This standalone component manages the dashboard's header, sidebar,
 * routed content, and toast notifications. It also tracks the current
 * screen size and automatically adjusts the sidebar collapsed state
 * when the viewport crosses mobile, tablet, and desktop breakpoints.
 */
@Component({
  selector: 'zba-dashboard-layout',
  standalone: true,
  imports: [SidebarComponent, HeaderComponent, RouterOutlet, ToastComponent],
  templateUrl: './dashboard-layout.component.html',
  styleUrl: './dashboard-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardLayoutComponent implements OnInit {
  /**
   * Reactive state indicating whether the dashboard sidebar is collapsed.
   * The sidebar is automatically collapsed on small and medium screens
   * and expanded on desktop when crossing responsive breakpoints.
   */
  readonly isSidebarCollapsed = signal<boolean>(false);

  /**
   * Reactive state indicating whether the current viewport
   * is considered a small/mobile screen.
   * Small screens have a width of 768px or less.
   */
  readonly isSmallScreen = signal<boolean>(false);

  /**
   * Reactive state indicating whether the current viewport
   * is considered a medium/tablet screen.
   * Medium screens have a width greater than 768px and
   * less than or equal to 1024px.
   */
  readonly isMediumScreen = signal<boolean>(false);

  /**
   * Stores the previous viewport width.
   * This value is used to detect when the viewport crosses
   * between desktop and mobile/tablet breakpoints.
   * A default width of 1200px is used when the `window` object
   * is unavailable, such as during server-side rendering.
   */
  private previousWidth =
    typeof window !== 'undefined' ? window.innerWidth : 1200;

  /**
   * Initializes the dashboard layout.
   * Performs the initial screen-size calculation and sets the
   * appropriate sidebar state based on the current viewport width.
   */
  ngOnInit(): void {
    this.handleScreenResize(true);
  }

  /**
   * Handles browser window resize events.
   * The method recalculates the current responsive breakpoint
   * and updates the sidebar state when the viewport crosses
   * between desktop and mobile/tablet layouts.
   */
  @HostListener('window:resize')
  onResize(): void {
    this.handleScreenResize(false);
  }

  /**
   * Updates responsive screen-state signals and adjusts the
   * sidebar collapsed state based on the current viewport width.
   * On the initial load, the sidebar is collapsed for mobile and
   * tablet screens and expanded for desktop screens.
   * During subsequent resizes, the sidebar state is automatically
   * adjusted only when the viewport crosses the desktop breakpoint.
   * @param isInitial Indicates whether the resize handling is being
   * performed during component initialization.
   */
  private handleScreenResize(isInitial: boolean): void {
    if (typeof window === 'undefined') return;

    const width = window.innerWidth;
    const isSmall = width <= 768;
    const isMedium = width > 768 && width <= 1024;
    const isDesktop = width > 1024;

    this.isSmallScreen.set(isSmall);
    this.isMediumScreen.set(isMedium);

    if (isInitial) {
      // On initial load: Collapse on mobile & tablet, expand on desktop
      this.isSidebarCollapsed.set(isSmall || isMedium);
    } else {
      /**
       * Determines whether the previous viewport was in the
       * desktop breakpoint.
       */
      const wasDesktop = this.previousWidth > 1024;

      /**
       * Determines whether the previous viewport was in the
       * tablet or mobile breakpoint.
       */
      const wasTabletOrMobile = this.previousWidth <= 1024;

      if (isDesktop && wasTabletOrMobile) {
        /**
         * The viewport transitioned from mobile/tablet to desktop.
         * Expand the sidebar automatically.
         */
        this.isSidebarCollapsed.set(false);
      } else if (!isDesktop && wasDesktop) {
        /**
         * The viewport transitioned from desktop to mobile/tablet.
         * Collapse the sidebar automatically.
         */
        this.isSidebarCollapsed.set(true);
      }
    }

    /**
     * Stores the current viewport width so it can be used as
     * the previous width during the next resize event.
     */
    this.previousWidth = width;
  }
}